<template>
  <div>
    <Modal
      width="400"
      v-model="displayDeleteAccountModal"
      :title="$t('settings.delete_account_modal_title')"
      :subtitle="$t('settings.delete_account_modal_subtitle')"
      :dialog-icon="'error'"
      :cta-text="$t('settings.delete_account_modal_cta_text')"
      :cta-color="'error'"
      :cta-loading="deleteAccountLoading"
      :cta-disabled="confirmationWord !== USER_DELETE_CONFIRMATION_WORD"
      @confirm="deleteAccount"
      @cancel="closeDeleteAccountModal">
      <p class="delete-confirm">
        {{ $t("settings.delete_account_modal_confirmation", { word: USER_DELETE_CONFIRMATION_WORD }) }}
      </p>
      <v-text-field class="delete-confirm__input" v-model="confirmationWord" hide-details label=""> </v-text-field>
    </Modal>

    <Modal
      width="400"
      v-model="displayChangePasswordModal"
      :title="$t('settings.change_password_modal_title')"
      :cta-text="$t('generic.buttons.save')"
      :cta-disabled="!allowChangePasswordButton"
      :cta-loading="passwordChangeLoading"
      @confirm="changePassword"
      @cancel="closeChangePasswordModal">
      <div class="spacing-top--20">
        <v-text-field
          class="password-input"
          :placeholder="$t('settings.current_password')"
          :label="$t('settings.current_password')"
          validate-on-blur
          persistent-placeholder
          hide-details="auto"
          autocomplete="current-password"
          v-model="changePasswordForm.currentPassword"
          :append-icon="hidePassword ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
          :rules="[rules.required]"
          ref="password"
          :type="hidePassword ? 'password' : 'text'"
          @click:append="() => (hidePassword = !hidePassword)"
          required>
          <template v-slot:message="{ message }">
            <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
            {{ message }}
          </template>
        </v-text-field>
      </div>
      <div class="auth-forgot" @click="forgotPasswordHandler">
        {{ $t("auth.forgot_password") }}
      </div>

      <PasswordAndPasswordRepeatBlock
        class="password-score-row"
        v-model="changePasswordForm.newPassword"
        :fixed-popper-position="'top'"
        :clean-inputs="cleanInputs"
        @passwordInputsValid="setPasswordInputsCheck" />
    </Modal>

    <VaDisclaimer
      v-if="displayVerifyEmailDisclaimer"
      class="verify-email__disclaimer spacing-bottom--24"
      :type="'warning'"
      :title="$t('settings.verify_email_title')"
      :text="$t('settings.verify_email_subtitle', { email: userData.email })">
      <template #actions>
        <v-btn
          data-cy="resend-verify-email-btn"
          :loading="emailResendLoading"
          @click="resendEmail"
          outlined
          color="same-menu"
          height="38">
          {{ $t("settings.resend_email") }}
        </v-btn>
      </template>
    </VaDisclaimer>

    <v-card :class="[!displayVerifyEmailDisclaimer ? 'mt-6' : '', 'spacing-bottom--16']">
      <v-form v-model="isFormValid">
        <div class="d-flex">
          <div class="d-flex flex-column">
            <v-card-title>{{ $t("settings.user_details_title") }}</v-card-title>
            <v-card-subtitle> {{ $t("settings.user_details_subtitle") }}</v-card-subtitle>
          </div>
          <v-card-actions class="ml-auto">
            <v-btn
              data-cy="user-profile-update-btn"
              :loading="updateProfileLoading"
              @click="updateAccount"
              :disabled="!isFormValid"
              color="primary"
              outlined
              >Save</v-btn
            >
          </v-card-actions>
        </div>
        <va-text-input
          required
          data-cy="user-firstname-input"
          class="spacing-bottom--16"
          v-model="userData.firstName"
          :label="$t('settings.user_first_name')"
          :placeholder="$t('settings.user_first_name_placeholder')"></va-text-input>
        <va-text-input
          required
          data-cy="user-lastname-input"
          class="spacing-bottom--16"
          v-model="userData.lastName"
          :label="$t('settings.user_last_name')"
          :placeholder="$t('settings.user_last_name_placeholder')"></va-text-input>
        <va-text-input
          data-cy="user-email-input"
          class="spacing-bottom--16"
          v-model="userData.email"
          :rules="[v => !!validateEmail(v) || this.$t('generic.validation.email')]"
          :label="$t('settings.user_email')"
          disabled
          :placeholder="$t('settings.user_email_placeholder')"></va-text-input>
        <va-select-input
          data-cy="user-role-input"
          v-model="userData.role"
          :label="$t('settings.user_role')"
          :placeholder="$t('settings.user_role_placeholder')"
          :choices="USER_ROLES"
          :item-text="'title'"
          :item-value="'value'"
          disabled
          :hide-details="'auto'">
          <template #info>
            <info-tooltip :info="roleTooltipText"></info-tooltip>
          </template>
        </va-select-input>
        <TimezonePicker required class="mt-3" v-model="userData.timezone" :label="$t('profile.timezone')" />
      </v-form>
      <div v-if="$can('view', 'login to airbyte')">
        <div class="airbyte-wrapper">
          <span>
            {{ $t("settings.login_to_airbyte_fb_title") }}
          </span>
          <v-btn outlined @click="airbyteInit('facebook')" :loading="airbyteLoading" id="fb-button">
            <img :src="facebookLogo" alt="Facebook Logo" />
            {{ $t("settings.login_to_airbyte_fb") }}
          </v-btn>
        </div>
      </div>
      <div v-if="$can('view', 'login to airbyte')">
        <div class="airbyte-wrapper">
          <span>
            {{ $t("settings.login_to_airbyte_google_title") }}
          </span>
          <v-btn outlined @click="airbyteInit('google')" :loading="airbyteLoading" id="google-button">
            <img :src="googleLogo" alt="Google Logo" />
            {{ $t("settings.login_to_airbyte_google") }}
          </v-btn>
        </div>
      </div>
      <AirbyteResourcesList
        v-if="$can('view', 'login to airbyte')"
        :list-page="false"
        resource-name="airbyte_sources" />
    </v-card>

    <v-card class="spacing-bottom--16">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("settings.user_password_title") }}</v-card-title>
          <v-card-subtitle class="mb-0"> {{ $t("settings.user_password_subtitle") }}</v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-btn
            data-cy="request-password-change-btn"
            @click="displayChangePasswordModal = true"
            color="primary"
            outlined>
            {{ $t("settings.user_password_change") }}
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>

    <v-card class="spacing-bottom--16">
      <v-card-title>{{ $t("settings.appearance_title") }}</v-card-title>
      <v-card-subtitle>{{ $t("settings.appearance_subtitle") }}</v-card-subtitle>
      <AppCustomizer />
    </v-card>

    <v-card class="spacing-bottom--16">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("settings.user_delete_account_title") }}</v-card-title>
          <v-card-subtitle class="mb-0">
            {{ deleteAccountSubtitle }}
          </v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-btn
            :disabled="userData.role == ROLE_OWNER.value"
            data-cy="delete-account-btn"
            :loading="deleteAccountLoading"
            @click="displayDeleteAccountModal = true"
            color="headline"
            outlined>
            {{ $t("settings.user_delete_account_action_text") }}
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </div>
</template>

<script>
  // Components
  import AppCustomizer from "@core/layouts/components/app-customizer/AppCustomizer.vue";
  import TimezonePicker from "@/components/custom/TimezonePicker.vue";
  import VaDisclaimer from "@/components/custom/VaDisclaimer";
  import InfoTooltip from "@/components/custom/InfoTooltip";
  import Modal from "@/components/custom/Modal";
  import PasswordAndPasswordRepeatBlock from "@/components/custom/PasswordAndPasswordRepeatBlock.vue";
  // Constants
  import { USER_ROLES, ROLE_OWNER, ROLE_ADMIN, ROLE_EDITOR, USER_DELETE_CONFIRMATION_WORD } from "@/constant";
  // Vuex
  import store from "@/store";
  import { mapGetters, mapActions } from "vuex";
  // Helpers
  import { getMainRole } from "@/assets/utilities/helpers/roles";
  import { validateEmail } from "@/assets/utilities/helpers/email";
  // Icons
  import { mdiEyeOffOutline, mdiEyeOutline, mdiAlertCircle } from "@mdi/js";
  import googleLogo from "@/assets/icons/google_logo.svg";
  import facebookLogo from "@/assets/icons/fb_logo.svg";
  import AirbyteResourcesList from "@/resources/airbyte-sources/List";
  // Utility
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "Profile",
    components: {
      VaDisclaimer,
      InfoTooltip,
      TimezonePicker,
      AppCustomizer,
      Modal,
      PasswordAndPasswordRepeatBlock,
      AirbyteResourcesList,
    },
    data() {
      return {
        icons: {
          mdiEyeOutline: mdiEyeOutline,
          mdiEyeOffOutline: mdiEyeOffOutline,
          mdiAlertCircle: mdiAlertCircle,
        },
        googleLogo,
        facebookLogo,
        validateEmail,
        USER_ROLES,
        ROLE_OWNER,
        ROLE_ADMIN,
        ROLE_EDITOR,
        userData: {
          firstName: "",
          lastName: "",
          email: "",
          role: "",
          timezone: "",
          id: "",
          verified: true,
        },
        // Loading states
        passwordChangeLoading: false,
        emailResendLoading: false,
        updateProfileLoading: false,
        deleteAccountLoading: false,
        airbyteLoading: false,
        isFormValid: false,
        displayDeleteAccountModal: false,
        displayChangePasswordModal: false,
        USER_DELETE_CONFIRMATION_WORD,
        confirmationWord: "",
        changePasswordForm: {
          currentPassword: "",
          newPassword: "",
        },
        hidePassword: true,
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
        },
        passwordInputsValid: false,
        cleanInputs: false,
      };
    },
    computed: {
      ...mapGetters({
        currentOrganization: "getActiveOrganization",
      }),
      roleTooltipText() {
        if (this.userData.role === ROLE_OWNER.value) return this.$t("settings.owner_tooltip_text");
        if (this.userData.role === ROLE_ADMIN.value) return this.$t("settings.admin_tooltip_text");
        if (this.userData.role === ROLE_EDITOR.value) return this.$t("settings.editor_tooltip_text");

        return "";
      },
      deleteAccountSubtitle() {
        if (this.$can("view", "delete account explanation")) return this.$t("settings.user_delete_account_explanation");

        return this.userData.role == ROLE_OWNER.value
          ? this.$t("settings.user_delete_account_disabled_subtitle")
          : this.$t("settings.user_delete_account_subtitle");
      },
      displayVerifyEmailDisclaimer() {
        return !this.userData.verified && this.$can("view", "email verify");
      },
      allowChangePasswordButton() {
        return this.passwordInputsValid && !!this.changePasswordForm.currentPassword.length;
      },
    },
    watch: {
      currentOrganization: {
        immediate: true,
        handler(newOrganization) {
          this.userData.role = getMainRole(newOrganization?.organizationPermissions)?.name;
        },
      },
    },
    async beforeMount() {
      await this.getUserInfo();
    },
    methods: {
      ...mapActions({
        logout: "auth/logout",
      }),
      closeDeleteAccountModal() {
        this.displayDeleteAccountModal = false;
        this.confirmationWord = "";
      },
      closeChangePasswordModal() {
        this.displayChangePasswordModal = false;
      },
      async airbyteInit(source) {
        try {
          this.airbyteLoading = true;
          const response = await this.$admin.dataProvider.customHttpCall("POST", `airbyte_sources/${source}/init`);
          window.open(response.data.url, "_blank").focus();
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.airbyteLoading = false;
        }
      },
      async updateAccount() {
        this.updateProfileLoading = true;
        try {
          if (
            await this.$admin.dataProvider.update("users", {
              id: this.userData.id,
              data: {
                firstName: this.userData.firstName,
                lastName: this.userData.lastName,
                // TODO: Uncomment: Might be needed
                // email: this.userData.email,
                timezone: this.userData.timezone,
              },
            })
          ) {
            this.$admin.toast.success(this.$t("profile.account_updated"));
            await store.dispatch("refreshAndStoreAllUserData");
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("error")));
        } finally {
          this.updateProfileLoading = false;
        }
      },
      async getUserInfo() {
        const { id, email, firstName, lastName, timezone, verified } = this.$store.state.user;
        this.userData.id = id;
        this.userData.email = email;
        this.userData.firstName = firstName;
        this.userData.lastName = lastName;
        this.userData.timezone = timezone;
        this.userData.verified = verified;
      },
      async resendEmail() {
        this.emailResendLoading = true;
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `users/${this.userData.email}/verify/resend`,
          );

          if (data.result === "SUCCESS") {
            this.$admin.toast.success(data.message);
          }
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("generic.error")));
        } finally {
          this.emailResendLoading = false;
        }
      },
      async changePassword() {
        try {
          const response = await this.$admin.dataProvider.customHttpCall(
            "POST",
            "auth/change_password",
            this.changePasswordForm,
          );

          if (response.status === 200) {
            this.$admin.toast.success(this.$t("settings.password_changed_successfully"));
            this.displayChangePasswordModal = false;
            this.changePasswordForm.currentPassword = "";
            this.cleanInputs = true;
          }
        } catch (e) {
          this.$admin.toast.error(this.$t(e.description));
        }
      },
      setPasswordInputsCheck(value) {
        this.passwordInputsValid = value;
      },
      async forgotPasswordHandler() {
        try {
          const response = await this.$admin.dataProvider.customHttpCall("POST", "auth/reset_password", {
            email: this.userData.email,
          });
          if (response.status === 200) {
            this.$admin.toast.success(this.$t("settings.password_reset"));
          }
        } catch (e) {
          this.$admin.toast.error(this.$t(e.description));
        } finally {
          this.displayChangePasswordModal = false;
        }
      },
      async deleteAccount() {
        this.deleteAccountLoading = true;
        try {
          await this.$admin.dataProvider.delete(`users`, { id: this.userData.id });
          await this.logout();
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("error")));
        } finally {
          this.deleteAccountLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .verify-email__disclaimer {
    margin-top: 25px;
  }

  .delete-confirm {
    @include text-subtitle-2();
    color: get-color("body");
    margin-top: 24px;
    margin-bottom: 16px;
    &__input {
      padding-top: 0 !important;
    }
  }
  .password-input {
    ::v-deep {
      .v-input__slot {
        .v-input__icon {
          .v-icon--link {
            color: get-color("icon");
          }
        }
      }
    }
  }

  .auth-forgot {
    @include text-caption();
    color: get-color("primary");
    cursor: pointer;
    margin-top: 8px;

    &:hover {
      color: get-color("primary-hover");
    }
  }

  .airbyte-wrapper {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    span {
      @include text-caption-bold();
      color: get-color("subtitle");
    }
  }

  #google-button.v-btn {
    border: 1px solid get-color("border");
    padding: 12px;

    ::v-deep {
      .v-btn__content {
        @include text-button-small();
        color: get-color("subtitle");
        font-weight: 600;
        display: flex;
        gap: 10px;
        align-items: center;

        img {
          width: 18px;
          height: 18px;
        }
      }
    }
  }
  #fb-button.v-btn {
    border: 1px solid get-color("border");
    padding: 12px;

    ::v-deep {
      .v-btn__content {
        @include text-button-small();
        color: get-color("subtitle");
        font-weight: 600;
        display: flex;
        gap: 10px;
        align-items: center;

        img {
          width: 18px;
          height: 18px;
        }
      }
    }
  }
</style>
